import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";

import css from './DishAddSemiFinishModal.module.css';
import {dishSemiFinishedActions} from "../../../../store/slices/dishes-slices";
import {semiFinishedActions} from "../../../../store/slices/semiFinished-slice";
import { SearchableSelect } from "../../../../ui";

const DishAddSemiFinishModal = ({setDishSemiFinishedAddOpen}) => {
    const [selectedSemiFinished, setSelectedSemiFinished] = useState("");
    const dispatch = useDispatch();
    const {t, i18n} = useTranslation();
    const currentLanguage = i18n.language;

    const {semiFinishedArray, trigger} = useSelector(store => store.semiFinished);
    const {dishSemiFinishedModalArray} = useSelector(store => store.dishSemiFinished);

    const {
        register,
        handleSubmit,
        formState: {errors}
    } = useForm();

    useEffect(() => {
        dispatch(semiFinishedActions.getAllSemiFinished());
    }, [trigger, dispatch]);

    const handleAddSemiFinishSubmit = (data) => {
        const selectedItem = semiFinishedArray.find(obj => obj.id === selectedSemiFinished);
        if (!selectedItem) return;

        const weightNetto = Number(data.weightNetto);
        
        // Auto-calculate weight based on weightNetto and semi-finished spec
        const originalWeight = parseFloat(selectedItem.weight) || 0;
        const originalWeightNetto = parseFloat(selectedItem.weightNetto) || 0;
        const nettoRatio = originalWeight > 0 ? originalWeightNetto / originalWeight : 1;
        const weight = nettoRatio > 0 ? Number((weightNetto / nettoRatio).toFixed(2)) : weightNetto;

        // Calculate the portion multiplier based on weight (not weightNetto)
        // Semi-finished items store total nutrients for their entire weight
        const originalItemWeight = parseFloat(selectedItem.weight) || 100;
        const portionMultiplier = weight / originalItemWeight;

        // Calculate cost from component products + nested semi-finisheds
        const totalProductsCost = (selectedItem.products || []).reduce(
            (sum, p) => sum + (parseFloat(p.cost) || 0), 0
        );
        const totalChildSfCost = (selectedItem.includedSemiFinisheds || []).reduce(
            (sum, s) => sum + (parseFloat(s.cost) || 0), 0
        );
        const totalSemiFinishedCost = totalProductsCost + totalChildSfCost;

        const singleSemiFinished = {
            id: selectedItem.id,
            name: selectedItem.name,
            weight,
            weightNetto,
            cost: Number((totalSemiFinishedCost * portionMultiplier).toFixed(2)),
            // Semi-finished nutrients are stored as totals for the entire weight
            // Calculate proportionally based on weight used
            energy: Number(((selectedItem.energy || 0) * portionMultiplier).toFixed(2)),
            fats: Number(((selectedItem.fats || 0) * portionMultiplier).toFixed(2)),
            proteins: Number(((selectedItem.proteins || 0) * portionMultiplier).toFixed(2)),
            carbohydrates: Number(((selectedItem.carbs || 0) * portionMultiplier).toFixed(2)),
        };

        dispatch(dishSemiFinishedActions.addDishSemiFinishForModal(singleSemiFinished));
        dispatch(dishSemiFinishedActions.changeTrigger());
        setDishSemiFinishedAddOpen(false);
    };

    // Prepare options for SearchableSelect
    const semiFinishedOptions = semiFinishedArray.map(semiFinished => ({
        id: semiFinished.id,
        name: semiFinished.name,
        label: semiFinished.name[currentLanguage] || semiFinished.name['en']
    }));

    return (
        <section className={css.overlay}>
            <div className={css.modal}>
                <button className={css.closeButton} onClick={() => setDishSemiFinishedAddOpen(false)}>
                    &times;
                </button>
                <h2 className={css.title}>Add Semi Finish</h2>

                <form className={css.content} onSubmit={handleSubmit(handleAddSemiFinishSubmit)}>
                    <div className={css.inputGroup}>
                        <label htmlFor="group">Semi Finished</label>
                        <SearchableSelect
                            options={semiFinishedOptions}
                            value={selectedSemiFinished}
                            onChange={setSelectedSemiFinished}
                            placeholder={t('select-semi-finished')}
                            searchPlaceholder={t('search-semi-finished')}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.id}
                            isOptionDisabled={(option) => 
                                dishSemiFinishedModalArray.some(p => 
                                    p.id === option.id || p.semiFinishedId === option.id
                                )
                            }
                            className={css.searchableSelect}
                        />
                    </div>

                    {/* Only show WeightNetto field - Weight will be auto-calculated */}
                    <div className={css.inputGroup}>
                        <label htmlFor="weightNetto">{t('WeightNetto')}</label>
                        <input
                            type="number"
                            id="weightNetto"
                            {...register("weightNetto", { required: true })}
                            className={errors.weightNetto ? css.inputError : ""}
                        />
                        {errors.weightNetto && <p className={css.error}>{errors.weightNetto.message}</p>}
                    </div>

                    <div className={css.actions}>
                        <button type="button" className={css.cancelButton} onClick={() => setDishSemiFinishedAddOpen(false)}>
                            Cancel
                        </button>
                        <button type="submit" className={css.okButton}>OK</button>
                    </div>
                </form>
            </div>
        </section>
    );
};

export {DishAddSemiFinishModal};
